import Dexie, { Table } from 'dexie';
import * as LZString from 'lz-string';

export interface ICacheEntry {
    key: string;
    data: string; // LZ-compressed JSON
    originalSize: number;
    compressedSize: number;
    timestamp: number;
    version: string;
}

export class IndexedDBCacheService extends Dexie {
    public cache!: Table<ICacheEntry, string>;
    private cumulativeCompressedSize: number = 0;

    constructor() {
        super('CopilotDashboardCache');
        this.version(1).stores({
            cache: 'key, timestamp'
        });
        console.log('[CopilotDexieDB] Service initialized');
    }

    public async put(key: string, data: any, version: string): Promise<void> {
        try {
            const rawJson = JSON.stringify(data);
            const originalSize = rawJson.length * 2;
            const compressedString = LZString.compressToBase64(rawJson);
            const compressedSize = compressedString.length * 2;
            this.cumulativeCompressedSize += compressedSize;

            const reductionRatio = originalSize > 0
                ? (100 - (compressedSize / originalSize) * 100).toFixed(2)
                : '0.00';

            console.log(
                `[CopilotDexieDB] PUT: '${key}' | ` +
                `Original: ${this._formatBytes(originalSize)} | ` +
                `Compressed: ${this._formatBytes(compressedSize)} | ` +
                `Reduction: ${reductionRatio}% | ` +
                `Cumulative: ${this._formatBytes(this.cumulativeCompressedSize)}`
            );

            const entry: ICacheEntry = {
                key,
                data: compressedString,
                originalSize,
                compressedSize,
                timestamp: Date.now(),
                version
            };

            await this.cache.put(entry);
        } catch (e) {
            console.error(`[CopilotDexieDB] Error storing key: ${key}`, e);
        }
    }

    public async get(key: string, version?: string): Promise<any | undefined> {
        try {
            const entry = await this.cache.get(key);
            if (!entry) {
                return undefined;
            }

            if (version && entry.version !== version) {
                await this.cache.delete(key);
                return undefined;
            }

            const decompressed = LZString.decompressFromBase64(entry.data);
            if (decompressed) {
                return JSON.parse(decompressed);
            }
            return undefined;
        } catch (e) {
            console.error(`[CopilotDexieDB] Error retrieving key: ${key}`, e);
            return undefined;
        }
    }

    private _formatBytes(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
}

let cacheServiceInstance: IndexedDBCacheService | undefined = undefined;

export function getCopilotDexieDB(): IndexedDBCacheService {
    if (!cacheServiceInstance) {
        cacheServiceInstance = new IndexedDBCacheService();
    }
    return cacheServiceInstance;
}
